import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import {
	PrismInputComponent,
	PrismRadioButtonComponent,
	PrismRadioGroupComponent,
	TextValueAccessor
} from '@shure/prism-angular-components';
import { getRoomPasswordValidators } from '@shure/shared/angular/ui/validators/common';
import { MatchValidator } from '@shure/shared/angular/ui/validators/match';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

export interface DevicePasswordConfigurationOptions {
	keepExisting?: boolean;
	dontSet?: boolean;
	removeAll?: boolean;
}

export enum PasswordConfigurationOption {
	NewCommonPassword = 'NewCommonPassword',
	KeepExisting = 'KeepExisting',
	DontSetPassword = 'DontSetPassword',
	RemoveAll = 'RemoveAll'
}

@Component({
	selector: 'sh-device-password-configuration',
	imports: [
		CommonModule,
		TranslocoModule,
		ReactiveFormsModule,
		FlexLayoutModule,
		MatFormFieldModule,
		PrismRadioGroupComponent,
		PrismRadioButtonComponent,
		PrismInputComponent,
		TextValueAccessor
	],
	providers: [provideInlineScope('scDeviceAuth', (lang) => import(`../../../../../i18n/${lang}`))],
	templateUrl: './device-password-configuration.component.html',
	styleUrls: ['./device-password-configuration.component.scss']
})
export class DevicePasswordConfigurationComponent implements OnInit, OnDestroy {
	protected passwordOption = PasswordConfigurationOption;

	/**
	 * Emits the current selected password option.
	 */
	@Output()
	public readonly passwordOptionChanged = new EventEmitter<PasswordConfigurationOption>();

	/**
	 * Emits on valid password change.
	 * Emits 'undefined' if 'password' and 'confirmPassword' do not match
	 */
	@Output()
	public readonly passwordChanged = new EventEmitter<string | undefined>();
	@Output()
	public readonly enterKeyPressed = new EventEmitter<void>();

	/**
	 * Disables the entire component
	 */
	@Input()
	public set disabled(value: boolean) {
		this._disabled = value;
		this.disablePassowrdForms(value);
	}
	public get disabled(): boolean {
		return this._disabled;
	}
	private _disabled = false;

	/**
	 * Selects which options for password setting, should be visible.
	 * If all options are set to false only passwords fields are shown and the component defaults to setting a common password
	 */
	@Input()
	public options: DevicePasswordConfigurationOptions = {
		keepExisting: false,
		dontSet: false,
		removeAll: false
	};

	@Input() public descriptionSetCommonOption!: string;

	protected passwordFormGroup = new FormGroup(
		{
			password: new FormControl('', {
				validators: getRoomPasswordValidators(),
				nonNullable: true
			}),
			confirmPassword: new FormControl('', { nonNullable: true })
		},
		{
			validators: [MatchValidator.matches('password', 'confirmPassword', true)]
		}
	);

	private destroy$ = new Subject<void>();

	public ngOnInit(): void {
		this.passwordOptionChanged.emit(PasswordConfigurationOption.NewCommonPassword);

		this.passwordFormGroup.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(({ password, confirmPassword }) => {
				if (password !== undefined && confirmPassword !== undefined && this.passwordFormGroup.valid) {
					this.passwordChanged.emit(confirmPassword);
				} else {
					this.passwordChanged.emit(undefined);
				}
			});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	protected onOptionSelectionChange(value: string): void {
		if (value === PasswordConfigurationOption.NewCommonPassword) {
			this.disablePassowrdForms(false);
		} else {
			this.disablePassowrdForms(true);
		}
		this.passwordOptionChanged.emit(<PasswordConfigurationOption | undefined>value);
	}

	protected onEnter(): void {
		this.enterKeyPressed.emit();
	}

	private disablePassowrdForms(disable: boolean): void {
		if (disable) {
			this.passwordFormGroup.disable();
		} else {
			this.passwordFormGroup.enable();
		}
	}
}
